import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { roleType, wsStatus, AckMode } from "@/constants";
import { ErrorShowType, isNetworkAvailable } from "@/utils/request";
import useChatMessages from "@/hooks/useChatMessages";
import useChatConnection from "@/hooks/useChatConnection";
import { useAgentInitList } from "@/hooks/useAgentInitList";
import ChatWindow from "@/components/ChatWindow";
import ChatLauncher from "@/components/ChatLauncher";
import {
  getAgentFromScript,
  getSiteTheme,
  scrollDistanceFun,
  applyBrandColor,
} from "@/utils";
import { openSession } from "./services";
import { useFileUpload } from "./contexts/FileUploadContext";
import dayjs from "dayjs";
import { useI18n } from "./contexts/I18nContext";
import "./App.scss";
import styles from "@/components/ChatWindow/index.module.scss";

const App = () => {
  const [isLauncherVisible, setIsLauncherVisible] = useState(true);
  const [isHidden, setIsHidden] = useState(false); //设置弹窗可见或者不可见，用于点击减号的时候不销毁dom结构，保持数据的完整性
  const [openModal, setOpenModal] = useState(false);
  const [svgLoading, setSvgLoading] = useState(false);
  const dialogueContentRef = useRef<HTMLDivElement>(null);
  const { formatMessage, setLocale } = useI18n();
  const [agentData, setAgentData] = useState<any>({});
  const [isCloseSession, setIsCloseSession] = useState(false);
  const agentId = getAgentFromScript();
  const [curMode, setCurMode] = useState<string | null>(null);
  const [curCsIdentifier, setCurCsIdentifier] = useState(""); //保存当前客服凭证信息
  const [file, setFile] = useState<any>(null);
  const { fileUploading } = useFileUpload();
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 请求超时定时器
  const retryCountRef = useRef<number>(0); // 记录重试次数
  const sentFileRef = useRef<string | null>(null); // 记录已发送的文件ID，防止重复发送
  const REQUEST_TIMEOUT = 40000; // 40秒超时时间
  const MAX_RETRY_COUNT = 1; // 最大重试次数

  //自定义hook
  const chatMessages = useChatMessages();
  const chatConnection = useChatConnection();

  const {
    dialogueMessages,
    setDialogueMessages,
    addUserMessage,
    addAssistantMessage,
    updateMessage,
    removeMessage,
    generateUuid,
    addNewChatMessage,
  } = chatMessages;

  const {
    connect,
    sendMessage: sendWsMessage,
    lastMessage,
    connectionStatus,
    sseSourceRef,
    fetchLoading,
    setFetchLoading,
    baseUrl,
    getWebSocketUrl,
    closeSSE,
  } = chatConnection;

  //主题设置
  useEffect(() => {
    // 获取当前页面的主域名
    const host = window.location.host;
    //获取对应的主题
    const theme = getSiteTheme(host);
    document
      .getElementById("ai-customer-service")
      ?.setAttribute("data-theme", theme);
  }, []);

  // 组件卸载时清除超时定时器
  useEffect(() => {
    return () => {
      clearRequestTimeout();
    };
  }, []);

  //打开会话接口
  const initOpenSession = async () => {
    try {
      await openSession({ agent: getAgentFromScript() });
    } catch (error) {
      console.log(error);
    }
  };

  // 处理接收到的消息
  useEffect(() => {
    if (lastMessage && connectionStatus === wsStatus.OPEN) {
      handleWebSocketMessageRef.current(lastMessage);
    }
  }, [lastMessage, connectionStatus]);

  useEffect(() => {
    //监听ws状态，如果当前状态为已关闭，切换用户聊天模式
    if (connectionStatus === wsStatus.CLOSED) {
      setCurMode(null);
    }
  }, [connectionStatus]);

  //会话恢复逻辑, 关闭会话重新打开需要调open接口
  useEffect(() => {
    if (openModal && isCloseSession) initOpenSession();
  }, [openModal, isCloseSession]);

  useEffect(() => {
    if (isHidden) {
      setIsLauncherVisible(true);
    }
  }, [isHidden]);

  // 使用统一的自定义 hook 获取智能体初始化数据
  const { getAgentInitList } = useAgentInitList({
    setAgentData,
    setDialogueMessages,
    setLocale,
    applyBrandColor,
    svgLoadingSetter: setSvgLoading,
    setOpenModal,
    setIsLauncherVisible,
  });

  // 滚动到底部的函数
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => scrollDistanceFun(dialogueContentRef));
  }, []);

  // 在获取数据后滚动到底部
  const fetchDataAndScroll = useCallback(async () => {
    await getAgentInitList();
    scrollToBottom();
  }, [getAgentInitList, scrollToBottom]);

  //错误信息处理
  const handleApiError = (
    res: any,
    formatMessage: any,
    removeMessage: (id: string | undefined) => void,
    messageId?: string | undefined,
  ) => {
    const errorCodes = [
      ErrorShowType.SILENT,
      ErrorShowType.WARN_MESSAGE,
      ErrorShowType.USERIPCHANGES,
    ];

    if (errorCodes.includes(res?.code)) {
      toast.error(res.err_msg);
      //重新初始化智能体
      getAgentInitList();
    } else if (res?.code === 400) {
      toast.error(formatMessage("systemErrorText"));
    } else {
      toast.error(res.err_msg);
    }
    removeMessage(messageId);
  };

  //呼叫客服
  const handleCallCs = async () => {
    //重新连接的时候，当服务器准备就绪，需要重新将当前模式切换为人工客服模式
    setCurMode(roleType.cs);
    const messageId = generateUuid();
    //服务端已准备好, 发送消息
    try {
      await sendWsMessage({
        id: messageId,
        type: "join",
        ack_mode: AckMode.ACK_REQUIRED,
      });
    } catch (error) {
      console.error(error);
    }
  };

  //客户端请求人工客服，客服端收到确认后返回给客户端的join信息
  const handleJoin = (body: any) => {
    if (body.ack_mode === AckMode.ACK_CONFIRMED) {
      //后端会返回当前客服信息，保存当前客服唯一标识
      setCurCsIdentifier(body?.data?.cs_identifier);
      addNewChatMessage(body?.data);
    } else if (body.ack_mode === AckMode.ACK_REQUIRED) {
      addNewChatMessage(body?.data);
    }
  };

  //收到聊天消息
  const handleChat = (body: any) => {
    if (
      [
        AckMode.ACK_NOT_REQUIRED,
        AckMode.ACK_REQUIRED,
        AckMode.ACK_CONFIRMED,
      ].includes(body.ack_mode)
    ) {
      addNewChatMessage(body?.data);
      scrollToBottom();
    }
  };

  //客服端手动结束对话
  const handleChatEnd = (body: any) => {
    if (body.ack_mode === AckMode.ACK_REQUIRED) {
      addNewChatMessage(body?.data);
      scrollToBottom();
      //结束对话需要切换到智能体模式
      setCurMode(null);
    }
  };

  //客服转接
  const handleTransfer = (body: any) => {
    if (body.ack_mode === AckMode.ACK_REQUIRED) {
      addNewChatMessage(body?.data);
      scrollToBottom();
    }
  };

  //客服消息撤回
  const handleRevoke = (body: any) => {
    try {
      const { ack_mode, data } = body;
      // 接收到客服端撤回的消息
      if (ack_mode === AckMode.ACK_REQUIRED) {
        const revokeMessageId = data?.revoke_message?.id; //被撤回的消息id
        if (revokeMessageId) {
          // 删除该消息
          removeMessage(revokeMessageId);
          // 然后再新增消息，使用函数式更新确保状态一致性
          setDialogueMessages((prev) => [...prev, ...data?.messages]);
        }
      }
    } catch (error) {
      console.error("消息接收失败!");
    }
  };

  // WebSocket 消息统一处理器
  const handleWebSocketMessage = async (body: any) => {
    try {
      switch (body.type) {
        //客服接入监听
        case "join":
          handleJoin(body);
          break;
        //聊天监听
        case "chat":
          handleChat(body);
          break;
        case "ready":
          handleCallCs();
          break;
        //客服转接监听
        case "transfer":
          handleTransfer(body);
          break;
        //客服撤回消息监听
        case "revoke":
          handleRevoke(body);
          break;
        //客服主动结束会话监听
        case "end":
          handleChatEnd(body);
          break;
      }
    } catch (error) {
      console.error(error);
    }
  };

  //使用ref保存方法
  const handleWebSocketMessageRef = useRef(handleWebSocketMessage);
  // 每次渲染后更新 ref 的值
  useEffect(() => {
    handleWebSocketMessageRef.current = handleWebSocketMessage;
  });

  // 清除请求超时定时器
  const clearRequestTimeout = () => {
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
  };

  // SSE 事件处理器（统一错误处理）
  const createSSEEventHandlers = (
    retryMessageId?: string | undefined, //重试的消息id
    assistantId?: string | undefined, //当前智能体消息id
  ) => {
    return {
      onMessage: async (event: MessageEvent) => {
        try {
          // 收到消息后清除超时定时器并重置重试计数
          clearRequestTimeout();
          retryCountRef.current = 0;

          const res = JSON.parse(event.data);
          if (res?.code !== 0) {
            handleApiError(res, formatMessage, removeMessage, assistantId);
            return;
          }
          setDialogueMessages((prev) => {
            const newMessages = [...prev];
            const lastIndex = newMessages.length - 1;
            const messageIndex = newMessages.findIndex(
              (msg) => msg.id === retryMessageId,
            );
            //更新当前重试的用户消息, 将重试的loading设置为false
            if (messageIndex !== -1) {
              newMessages[messageIndex] = {
                ...newMessages[messageIndex],
                ...(retryMessageId && { retryLoading: false }),
              };
            }
            //更新智能体消息回答
            newMessages[lastIndex] = {
              id: generateUuid(),
              ...res?.data?.messages?.[0],
              loading: false,
            };
            return newMessages;
          });
          //人工客服
          const mode = res.data?.messages?.[0]?.mode;
          if (mode === roleType.cs) {
            setCurMode(mode);
            //连接websocket
            connect(getWebSocketUrl(agentId));
          }
          scrollToBottom();
        } catch (error) {
          clearRequestTimeout();
          retryCountRef.current = 0;
          toast.error(formatMessage("errorText"));
          //删除智能体初始化消息
          removeMessage(assistantId);
        }
      },
      onEnd: () => {
        clearRequestTimeout();
        retryCountRef.current = 0;
        closeSSE();
        setDialogueMessages((prev) =>
          prev.map((msg, index) => ({
            ...msg,
            loading: false,
            retryLoading: false,
            // 如果是最后一条消息，添加 isShowAction 属性
            ...(index === prev.length - 1 ? { isShowAction: true } : {}),
          })),
        );
      },
      onError: () => {
        clearRequestTimeout();
        retryCountRef.current = 0;
        closeSSE();
        toast.error(formatMessage("networkAnomalyText"));
        removeMessage(assistantId);
      },
    };
  };

  // 处理发送请求
  const sendMessageRequest = async (
    values: any,
    retryMessageId?: string | undefined,
    userMessageId?: string | undefined, // 新增：用户消息ID，用于超时后删除
  ) => {
    //判断网关
    if (!isNetworkAvailable) {
      toast.error(formatMessage("networkAnomalyText"));
      retryCountRef.current = 0; // 重置重试计数
      return;
    }

    //重试更新当前用户消息
    if (retryMessageId) {
      updateMessage(retryMessageId, {
        retryLoading: true,
        needRetry: false,
      });
    }

    //添加初始化智能体消息
    const assistantMessage = addAssistantMessage();
    setFetchLoading(true);

    const params = new URLSearchParams({
      agent: agentId,
      ...values,
    }).toString();

    const source = new EventSource(
      `${baseUrl}/api/v1/agent/converse?${params}`,
      { withCredentials: true },
    );
    //保存sse实例
    sseSourceRef.current = source;
    const assistantId = assistantMessage?.id;
    const { onMessage, onEnd, onError } = createSSEEventHandlers(
      retryMessageId,
      assistantId,
    );
    source.addEventListener("message", onMessage);
    source.addEventListener("end", onEnd);
    source.addEventListener("error", onError);

    // 设置请求超时定时器
    clearRequestTimeout(); // 先清除之前的定时器
    requestTimeoutRef.current = setTimeout(() => {
      console.warn(`SSE请求超时，当前重试次数: ${retryCountRef.current}`);
      // 关闭当前SSE连接
      closeSSE();

      // 检查是否已达到最大重试次数
      if (retryCountRef.current >= MAX_RETRY_COUNT) {
        // 删除用户发送的消息和智能体消息
        setDialogueMessages((prev) => {
          return prev.filter(
            (msg) =>
              msg.id !== assistantId &&
              msg.id !== userMessageId &&
              msg.id !== retryMessageId,
          );
        });

        setFetchLoading(false);
        retryCountRef.current = 0; // 重置重试计数
        return;
      }

      // 显示超时提示
      toast.error(formatMessage("networkAnomalyText"));

      // 更新消息状态，准备重试
      setDialogueMessages((prev) => {
        const newMessages = [...prev];
        const messageIndex = newMessages.findIndex(
          (msg) => msg.id === retryMessageId,
        );

        if (messageIndex !== -1) {
          // 如果是重试消息，更新其状态
          newMessages[messageIndex] = {
            ...newMessages[messageIndex],
            retryLoading: false,
            needRetry: true,
          };
        }

        // 移除智能体的loading消息
        return newMessages.filter((msg) => msg.id !== assistantId);
      });

      setFetchLoading(false);

      // 增加重试计数
      retryCountRef.current += 1;

      // 自动重试请求
      setTimeout(() => {
        sendMessageRequest(values, retryMessageId, userMessageId);
      }, 1000); // 延迟1秒后重试
    }, REQUEST_TIMEOUT);
  };

  // 发送消息
  const handleSendMessage = async (values: any) => {
    const { question } = values;

    //接口请求加载
    if (fetchLoading) {
      toast.loading(formatMessage("loadingAnswerQuestions"));
      return;
    }

    //如果是人工客服聊天，不走SSE请求
    if (curMode === roleType.cs) {
      //判断网关
      if (!isNetworkAvailable) {
        toast.error(formatMessage("networkAnomalyText"));
        return;
      }
      const wsParams = {
        id: generateUuid(),
        type: "chat",
        ack_mode: AckMode.ACK_REQUIRED,
        data: {
          cs_identifier: curCsIdentifier,
          messages: [
            {
              id: generateUuid(),
              role: roleType.user,
              message: question,
              tips: "",
              component: "",
              create_time: dayjs().unix(),
              attachments: file ? [file] : [],
            },
          ],
        },
      };
      await sendWsMessage(wsParams);
      // 发送消息后立即清空文件状态，防止重复发送
      if (file) {
        sentFileRef.current = file?.uid || file?.name;
        setFile(null);
      }
      return;
    }

    //添加用户消息
    const userMessage = addUserMessage(question);
    scrollToBottom();
    //发送sse请求，传入用户消息ID用于超时删除
    sendMessageRequest(values, undefined, userMessage?.id);
  };

  // 使用ref来存储handleSendMessage
  const handleSendMessageRef = useRef(handleSendMessage);
  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  });

  //监听上传文件列表数据，文件上传直接发送信息
  useEffect(() => {
    if (file && !fileUploading && file?.status === "done") {
      // 检查是否已经发送过这个文件，防止重复发送
      const fileId = file?.uid || file?.name;
      if (sentFileRef.current !== fileId) {
        sentFileRef.current = fileId;
        handleSendMessageRef.current({ question: "" });
      }
    }
  }, [file, fileUploading]);

  // 处理消息重试
  const handleRetryMessage = (retryMessageId: string) => {
    const targetMessage = dialogueMessages.find(
      (msg) => msg.id === retryMessageId,
    );
    const curMessage = {
      ...targetMessage,
      question: targetMessage?.message,
    };
    if (targetMessage) {
      // 重置重试计数
      retryCountRef.current = 0;
      sendMessageRequest(curMessage, retryMessageId, retryMessageId);
    }
  };

  // 关闭聊天窗口
  const handleCloseCard = () => {
    setOpenModal(false);
    //关闭人工客服模式
    setCurMode(null);
    setIsLauncherVisible(true);
    //清除上传文件
    setFile(null);
    //清除已发送文件记录
    sentFileRef.current = null;
  };

  //打开聊天窗口
  const handleOpenChat = useCallback(() => {
    //当打开弹窗的时候，需要判断弹窗是否有隐藏属性，如果有，则不调用初始化智能体的接口
    if (openModal) {
      if (isHidden) {
        setIsLauncherVisible(false); // 立即隐藏启动器
        setIsHidden(false);
      }
      return;
    }
    fetchDataAndScroll();
  }, [fetchDataAndScroll, isHidden, openModal]);

  useEffect(() => {
    const aiAgent = window.AIAgent || {
      __openQueue: [],
      open: () => undefined,
    };
    const pendingOpenCount = Array.isArray(aiAgent.__openQueue)
      ? aiAgent.__openQueue.length
      : 0;

    aiAgent.__openQueue = [];
    aiAgent.__openHandler = handleOpenChat;
    aiAgent.open = handleOpenChat;
    window.AIAgent = aiAgent;

    if (pendingOpenCount > 0) {
      handleOpenChat();
    }

    return () => {
      if (!window.AIAgent) {
        return;
      }

      if (window.AIAgent.__openHandler === handleOpenChat) {
        delete window.AIAgent.__openHandler;
      }

      if (window.AIAgent.open !== handleOpenChat) {
        return;
      }

      window.AIAgent.open = () => {
        const currentAiAgent = window.AIAgent || {
          __openQueue: [],
          open: () => undefined,
        };
        currentAiAgent.__openQueue = Array.isArray(
          currentAiAgent.__openQueue,
        )
          ? currentAiAgent.__openQueue
          : [];
        currentAiAgent.__openQueue.push("open");
        window.AIAgent = currentAiAgent;
      };
    };
  }, [handleOpenChat]);

  return (
    <>
      {openModal && (
        <div
          id={styles.aiServeModal}
          className={isHidden ? `${styles.hidden}` : ""}
        >
          <ChatWindow
            agentData={agentData}
            dialogueMessages={dialogueMessages}
            dialogueContentRef={dialogueContentRef}
            curMode={curMode}
            onClose={handleCloseCard}
            onRetry={handleRetryMessage}
            onFileChange={setFile}
            onSendMessage={handleSendMessage}
            setDialogueMessages={setDialogueMessages}
            setIsCloseSession={setIsCloseSession}
            closeSSE={closeSSE}
            scrollToBottom={scrollToBottom}
            hadleModalHidden={setIsHidden} //控制弹窗的可见性
            getAgentInitList={getAgentInitList} //传递获取智能体初始化数据的函数
          />
        </div>
      )}
      {isLauncherVisible && (
        <ChatLauncher
          loading={svgLoading}
          isLauncherVisible={isLauncherVisible}
          onClick={handleOpenChat}
        />
      )}
    </>
  );
};

export default App;
